import { useMemo, useRef, useState } from 'react';
import { cn, qty } from '../lib/utils';

/* Single-series line over time, plain SVG.
   points: [{ key, label, value, detail: [{ label, value }] }], already in order.
   One measure per chart (one axis). Crosshair + tooltip on touch/pointer. */
const W = 360;
const H = 150;
const PAD = { top: 18, right: 12, bottom: 22, left: 34 }; // room for the end label above a point at the top tick

const niceCeil = (v) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
};

export default function LineChart({ points, unit = '', className }) {
  const svgRef = useRef(null);
  const [active, setActive] = useState(null);

  const geo = useMemo(() => {
    const n = points.length;
    const max = niceCeil(Math.max(0, ...points.map((p) => p.value)));
    const x = (i) => PAD.left + (n <= 1 ? 0 : (i / (n - 1)) * (W - PAD.left - PAD.right));
    const y = (v) => PAD.top + (1 - v / max) * (H - PAD.top - PAD.bottom);
    const line = points.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
    const area = n ? `M${x(0).toFixed(1)},${y(0).toFixed(1)} L${line.replace(/ /g, ' L')} L${x(n - 1).toFixed(1)},${y(0).toFixed(1)} Z` : '';
    const yTicks = [0, max / 2, max];
    const step = Math.max(1, Math.round(n / 4));
    const xTicks = points.map((p, i) => ({ i, label: p.tick })).filter((t, idx) => idx === 0 || idx === n - 1 || idx % step === 0);
    return { n, max, x, y, line, area, yTicks, xTicks };
  }, [points]);

  const locate = (clientX) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !geo.n) return;
    const vx = ((clientX - rect.left) / rect.width) * W;
    const t = Math.min(1, Math.max(0, (vx - PAD.left) / (W - PAD.left - PAD.right)));
    setActive(Math.round(t * (geo.n - 1)));
  };

  const last = geo.n - 1;
  const cur = active !== null ? points[active] : null;
  const tipLeft = active !== null ? (geo.x(active) / W) * 100 : 0;

  return (
    <div className={cn('relative', className)}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full touch-none select-none"
        role="img"
        aria-label={`${unit} per day`}
        onPointerMove={(e) => locate(e.clientX)}
        onPointerDown={(e) => locate(e.clientX)}
        onPointerLeave={(e) => e.pointerType === 'mouse' && setActive(null)} // a finger lifts after a tap: keep the readout
      >
        {/* recessive hairline grid + y labels in text ink */}
        {geo.yTicks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={geo.y(v)} y2={geo.y(v)} stroke="hsl(var(--line))" strokeWidth="1" />
            <text x={PAD.left - 6} y={geo.y(v) + 3.5} textAnchor="end" fontSize="10" fill="hsl(var(--ink-2))">{qty(v)}</text>
          </g>
        ))}
        {geo.xTicks.map((t) => (
          <text key={t.i} x={geo.x(t.i)} y={H - 6} textAnchor={t.i === 0 ? 'start' : t.i === last ? 'end' : 'middle'} fontSize="10" fill="hsl(var(--ink-2))">
            {t.label}
          </text>
        ))}
        {/* single series: area + 2px line */}
        {geo.n > 1 && <path d={geo.area} fill="hsl(var(--stock))" opacity="0.12" />}
        {geo.n > 1 && <polyline points={geo.line} fill="none" stroke="hsl(var(--stock))" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
        {/* crosshair */}
        {active !== null && <line x1={geo.x(active)} x2={geo.x(active)} y1={PAD.top} y2={H - PAD.bottom} stroke="hsl(var(--ink-2))" strokeWidth="1" />}
        {/* end marker, or the active point: >= 8px with a 2px surface ring */}
        {geo.n > 0 && (
          <circle cx={geo.x(active ?? last)} cy={geo.y(points[active ?? last].value)} r="4.5" fill="hsl(var(--stock))" stroke="hsl(var(--surface))" strokeWidth="2" />
        )}
        {/* value at the end, in text ink */}
        {geo.n > 0 && active === null && points[last].value > 0 && (
          <text x={geo.x(last) - 8} y={geo.y(points[last].value) - 8} textAnchor="end" fontSize="11" fontWeight="600" fill="hsl(var(--ink))">
            {qty(points[last].value)}
          </text>
        )}
      </svg>
      {cur && (
        <div
          className="pointer-events-none absolute top-0 z-10 max-w-[60%] rounded-lg bg-ink px-2.5 py-1.5 text-[12px] text-bg shadow-lg"
          style={{ left: `${tipLeft}%`, transform: `translateX(${tipLeft > 60 ? '-100%' : tipLeft < 20 ? '0' : '-50%'})` }}
        >
          <p className="font-semibold">{cur.label}</p>
          <p>
            <span className="font-cond text-[16px] font-bold">{qty(cur.value)}</span> {unit}
          </p>
          {cur.detail?.map((d) => (
            <p key={d.label} className="opacity-80">
              {d.value} {d.label}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
